<template>
  <div class="page-container">
    <md-app md-mode="fixed">
      <global-request-status />
      <md-app-content>
        <form novalidate class="md-layout sign-in-form" @submit.prevent="submit">
          <div
            class="md-layout-item card-list"
            style="max-width: 768px; margin: 0 auto;">
            <md-card class="md-elevation-10">
              <md-card-header>
                <h1 class="md-title">{{ title }}</h1>
              </md-card-header>
              <md-card-content v-if="mode === 'sign-in'">
                <md-field>
                  <md-icon>person</md-icon>
                  <label>User Name</label>
                  <md-input v-model="username" name="userName"></md-input>
                </md-field>
                <md-field>
                  <md-icon>password</md-icon>
                  <label>Password</label>
                  <md-input type="password" v-model="password" name="password" autocomplete="current-password"></md-input>
                </md-field>
                <div class="error" :style="{visibility: showError ? 'visible' : 'hidden'}">
                  <span>{{ errorMessage }}</span>
                </div>
                <div style="min-height: 5px;">
                  <md-progress-bar md-mode="indeterminate" v-if="sending" />
                </div>
              </md-card-content>
              <md-card-content v-else-if="mode === 'request'">
                <p>Enter your username and we’ll email reset instructions to the address on your account.</p>
                <md-field>
                  <md-icon>person</md-icon>
                  <label>User Name</label>
                  <md-input v-model.trim="username" name="resetUserName" autocomplete="username"></md-input>
                </md-field>
                <div class="status-message" v-if="statusMessage">{{ statusMessage }}</div>
                <div style="min-height: 5px;">
                  <md-progress-bar md-mode="indeterminate" v-if="sending" />
                </div>
              </md-card-content>
              <md-card-content v-else-if="mode === 'reset'">
                <p>Choose a new password with at least 12 characters.</p>
                <md-field>
                  <md-icon>password</md-icon>
                  <label>New Password</label>
                  <md-input type="password" v-model="newPassword" name="newPassword" autocomplete="new-password"></md-input>
                </md-field>
                <md-field>
                  <md-icon>password</md-icon>
                  <label>Confirm New Password</label>
                  <md-input type="password" v-model="confirmPassword" name="confirmPassword" autocomplete="new-password"></md-input>
                </md-field>
                <div class="error" v-if="errorMessage">{{ errorMessage }}</div>
                <div style="min-height: 5px;">
                  <md-progress-bar md-mode="indeterminate" v-if="sending" />
                </div>
              </md-card-content>
              <md-card-content v-else>
                <p class="status-message">Your password has been changed. You can now sign in.</p>
              </md-card-content>
              <md-card-actions v-if="mode === 'sign-in'">
                <md-button type="button" @click="showResetRequest">Forgot password?</md-button>
                <md-button type="submit" class="md-primary" :disabled="!canSignIn()">Sign In</md-button>
              </md-card-actions>
              <md-card-actions v-else-if="mode === 'request'">
                <md-button type="button" @click="showSignIn">Back to Sign In</md-button>
                <md-button type="submit" class="md-primary" :disabled="sending || !username">Send Reset Link</md-button>
              </md-card-actions>
              <md-card-actions v-else-if="mode === 'reset'">
                <md-button type="button" @click="showSignIn">Cancel</md-button>
                <md-button type="submit" class="md-primary" :disabled="!canResetPassword()">Change Password</md-button>
              </md-card-actions>
              <md-card-actions v-else>
                <md-button type="button" class="md-primary" @click="showSignIn">Sign In</md-button>
              </md-card-actions>
            </md-card>
          </div>
        </form>
      </md-app-content>
    </md-app>
  </div>
</template>

<script>
import axios from 'axios';
import GlobalRequestStatus from "@/components/GlobalRequestStatus.vue";

export default {
  name: "App",
  components: {
    GlobalRequestStatus,
  },
  data: () => ({
    mode: 'sign-in',
    resetToken: '',
    statusMessage: '',
    showError: false,
    errorMessage: '',
    sending: false,
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  }),
  created()
  {
    const token = new URLSearchParams(window.location.hash.substring(1)).get('resetToken');
    if (token)
    {
      this.resetToken = token;
      this.mode = 'reset';
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  },
  computed: {
    title()
    {
      if (this.mode === 'request') return 'Reset Password';
      if (this.mode === 'reset') return 'Choose a New Password';
      if (this.mode === 'complete') return 'Password Changed';
      return 'Sign In';
    }
  },
  methods: {
    submit()
    {
      if (this.mode === 'request') return this.requestPasswordReset();
      if (this.mode === 'reset') return this.resetPassword();
      return this.signIn();
    },
    showResetRequest()
    {
      this.mode = 'request';
      this.statusMessage = '';
      this.errorMessage = '';
    },
    showSignIn()
    {
      window.history.replaceState({}, document.title, window.location.pathname);
      this.mode = 'sign-in';
      this.resetToken = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.statusMessage = '';
      this.errorMessage = '';
      this.showError = false;
    },
    canSignIn()
    {
      return !this.sending && this.username.length > 0 && this.password.length > 0;
    },
    canResetPassword()
    {
      return !this.sending && this.newPassword.length >= 12 && this.newPassword === this.confirmPassword;
    },
    async requestPasswordReset()
    {
      try
      {
        this.sending = true;
        this.statusMessage = '';
        const response = await axios.post('api/auth/password-reset/request', { username: this.username });
        this.statusMessage = response.data.message;
      }
      finally
      {
        this.sending = false;
      }
    },
    async resetPassword()
    {
      try
      {
        this.sending = true;
        this.errorMessage = '';
        await axios.post('api/auth/password-reset/complete', {
          token: this.resetToken,
          password: this.newPassword,
          confirmPassword: this.confirmPassword
        });
        window.history.replaceState({}, document.title, window.location.pathname);
        this.resetToken = '';
        this.mode = 'complete';
      }
      catch(error)
      {
        this.errorMessage = error.response?.data?.message || 'We could not reset your password. Please request a new link.';
      }
      finally
      {
        this.sending = false;
      }
    },
    async setToken()
    {
      const response = await axios.post('api/auth/session/token');

      if (response.status == 200)
        window.localStorage.setItem('request-token', response.data.token);
    },
    async signIn()
    {
      try
      {
        this.showError = false;
        this.sending = true;
        const response = await axios.post('api/auth/session', { userName: this.username, password: this.password });

        if (response.status == 200)
        {
          await this.setToken();
          window.location.replace(response.data.url);
        }
      }

      catch(error)
      {
        if (error.response && error.response.status == 401)
              this.showError = true;

          if (error.response.data.reason == 'ACCOUNT_DISABLED')
              this.errorMessage = 'Your account is disabled. Please contact your administrator.';

          else
              this.errorMessage = 'Invalid username and/or password.';
      }

      finally
      {
        this.sending = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.progress {
  margin-left: -16px;
  margin-right: -16px;
  margin-top: -16px;
  margin-bottom: 16px;
}

.error
{
  text-align: right;
  color: red;
}

.status-message
{
  color: #2e7d32;
  line-height: 1.5;
}

.md-content {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sign-in-form {
  width: 100%;
  max-width: 500px;
}

.md-card {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>

<style lang="scss">
.md-radio .md-radio-label {
  height: auto;
}
</style>
